import { InPageConfirmationModal } from 'components/Modals/InPageConfirmationModal'
import { useEffect, useState } from 'react'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'

interface IProps {
  saveComment: (c: string) => void
  setInputBox: (i: boolean) => void
  showInputBox: boolean
  charLimit: number
  loading: boolean
  disabled?: boolean
  isSaveCompleted: boolean
}

interface StyleIProps {
  showInputBox: boolean
  isLoading: boolean
  showConfirmationModal: boolean
  charLeft: number
}

export const CommentInput = ({
  saveComment,
  setInputBox,
  charLimit,
  showInputBox,
  loading,
  isSaveCompleted,
  disabled,
}: IProps) => {
  const [comment, setComment] = useState('')
  const [charLeft, setCharLeft] = useState(0)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  useEffect(() => {
    if (isSaveCompleted) {
      setComment('')
      setCharLeft(0)
      setInputBox(false)
    }
  }, [isSaveCompleted])

  const handleCancel = () => {
    setComment('')
    setCharLeft(0)
    setInputBox(false)
    setShowConfirmationModal(false)
  }

  const handleValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value
    const charLeft = inputValue.length

    if (charLeft > charLimit) {
      return
    }
    setComment(inputValue)
    setCharLeft(charLeft)
  }

  const handleSaveComment = () => {
    saveComment(comment)
  }

  const handleConfirmationCancel = () => {
    setShowConfirmationModal(false)
  }

  return (
    <Div
      showInputBox={showInputBox}
      isLoading={loading}
      showConfirmationModal={showConfirmationModal}
      charLeft={charLeft}
    >
      {showConfirmationModal ? (
        <InPageConfirmationModal>
          <div className="confirmation-modal">
            <h6>Discard current comment?</h6>
            <p>Your new comment text will be lost. This cannot be undone.</p>
            <div className="confirmation-modal-footer">
              <Button
                radius="rounded"
                className="cancel-btn"
                size="small"
                variant="tertiary"
                onClick={handleConfirmationCancel}
              >
                Cancel
              </Button>
              <Button
                variant="danger-outline"
                radius="normal"
                size="small"
                className="discard-btn"
                onClick={handleCancel}
              >
                Discard
              </Button>
            </div>
          </div>
        </InPageConfirmationModal>
      ) : (
        ''
      )}

      <span className="add-comment-title">Add New Comment</span>
      <div className="text-area-container">
        <textarea
          disabled={loading || disabled}
          onFocus={() => setInputBox(true)}
          value={comment}
          placeholder={showInputBox ? 'Type comment text here..' : 'Click to add a comment...'}
          onChange={(e) => handleValueChange(e)}
        />
      </div>
      <div className="comments-btn-show">
        <span className="char-limit">
          <span style={{ color: charLeft < charLimit ? ' #459b25' : ' #b52400' }}>{charLeft} </span> / {charLimit}
        </span>
        <div className="buttons-container">
          <Button
            variant="tertiary"
            className="cancel-btn"
            onMouseDown={() => (comment.length > 0 ? setShowConfirmationModal(true) : setInputBox(false))}
          >
            Cancel
          </Button>
          <Button
            radius="normal"
            variant="primary"
            disabled={loading || !comment}
            onClick={handleSaveComment}
            className=" save-btn"
          >
            Save
          </Button>
        </div>
      </div>
    </Div>
  )
}

const Div = styled.div<StyleIProps>`
  .add-comment-title {
    display: ${({ showInputBox }) => (showInputBox ? 'block' : 'none')};
    margin: 0 4.5rem 0.125rem 0.125rem;
    font-family: Milliard;
    font-size: 0.8125rem;
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.grey.dark};
  }
  .text-area-container {
    position: relative;
    margin-bottom: 0.0625rem;
  }
  textarea:focus,
  input:focus,
  button:focus {
    outline: none;
  }
  textarea {
    height: ${({ showInputBox }) => (showInputBox ? '6.25rem' : '1.75rem')};
    resize: none;
    width: 100%;
    background-color: var(--ds-background-input, white);
    border: 1px solid var(--ds-border, #dfe1e6);
    border-radius: 5px;
    opacity: ${({ isLoading }) => (isLoading ? '0.5' : '1')};
    padding: 0.25rem 0.9375rem 0.125rem 0.75rem;
    border-radius: 0.3125rem;
    border: solid 1px ${({ theme }) => theme.colors.primary.lighter};
    background-color: ${({ theme }) => theme.colors.grey.lightest};
    font-family: ${({ theme }) => theme.fontFamily.milliard};
    font-size: 0.8125rem;
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.grey.neutral};
  }
  .comments-btn-show {
    display: ${({ showInputBox, showConfirmationModal }) => (showInputBox && !showConfirmationModal ? 'flex' : 'none')};
    justify-content: space-between;
    border-bottom: 1px solid #ecedee;
    margin-bottom: 0.9375rem;
    padding-bottom: 0.3125rem;
    .char-limit {
      padding: 0 1.9375rem 0 0;
      font-family: Milliard;
      font-size: 0.8125rem;
      font-weight: ${({ theme }) => theme.fontWeight.medium};
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      color: ${({ theme }) => theme.colors.grey.neutral};
    }
    .buttons-container {
      display: flex;
      align-items: center;

      .save-btn {
        width: 3.375rem;
        height: 1.5rem;
        padding: 0.375rem;
      }
      .cancel-btn {
        margin-right: 0.625rem;
      }
    }
  }
  .confirmation-modal {
    position: absolute;
    top: 0;
    z-index: 999;
    background: ${({ theme }) => theme.colors.info.lightest};
    text-align: center;
    margin-top: 0.3125rem;
    padding: 0.9375rem 3.125rem;
    h6 {
      font-family: ${({ theme }) => theme.fontFamily.milliard};
      font-size: 0.9375rem;
      font-weight: ${({ theme }) => theme.fontWeight.bold};
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: center;
      color: ${({ theme }) => theme.colors.primary.darker};
      margin-bottom: 0.3125rem;
    }
    p {
      font-family: ${({ theme }) => theme.fontFamily.milliard};
      font-size: 0.75rem;
      font-weight: ${({ theme }) => theme.fontWeight.normal};
      font-stretch: normal;
      font-style: normal;
      line-height: 1.25;
      letter-spacing: normal;
      text-align: center;
      color: ${({ theme }) => theme.colors.grey.darker};
    }
    button {
      height: 1.625rem;

      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      margin-top: 0.9375rem;
    }

    .cancel-btn {
      margin-right: 0.625rem;
    }
  }
  .confirmation-modal-footer {
    display: flex;
    justify-content: center;
  }
`
